import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  DollarSign, 
  Ship, 
  CheckCircle, 
  ArrowRight,
  Camera,
  FileText,
  Phone,
  Mail,
  User,
  Building,
  MapPin,
  Gauge,
  Calendar,
  Clock,
  Shield,
  Zap,
  TrendingUp,
  Loader2,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  {
    icon: TrendingUp,
    title: 'Maximum Value',
    description: 'Our global network ensures your equipment reaches the right buyers at the best price.'
  },
  {
    icon: Zap,
    title: 'Fast Process',
    description: 'Get a valuation within 24 hours. We handle all logistics and paperwork.'
  },
  {
    icon: Shield,
    title: 'Secure Transaction',
    description: 'Protected payments and professional handling of your valuable equipment.'
  },
  {
    icon: Ship,
    title: 'Global Reach',
    description: 'Access to buyers in 50+ countries through our established network.'
  }
];

const howItWorks = [
  {
    step: '01',
    title: 'Submit Details',
    description: 'Fill out the form with your equipment specifications, condition, and photos.'
  },
  {
    step: '02',
    title: 'Get Valuation',
    description: 'Our experts assess your equipment and provide a competitive market valuation.'
  },
  {
    step: '03',
    title: 'Accept Offer',
    description: 'Review our offer and terms. No obligation - accept only if you are satisfied.'
  },
  {
    step: '04',
    title: 'Get Paid',
    description: 'We arrange pickup/shipping and process payment via your preferred method including crypto.'
  }
];

const brands = ['Caterpillar', 'Cummins', 'Volvo Penta', 'MTU', 'Yanmar', 'John Deere', 'MAN', 'Mercury', 'Wärtsilä', 'Rolls-Royce', 'Other'];
const conditions = ['New/Unused', 'Like New', 'Excellent', 'Good Used', 'Fair', 'For Parts/Rebuild'];
const engineTypes = ['Marine Propulsion', 'Marine Generator', 'Auxiliary', 'Industrial', 'Other'];

export default function SellEquipmentPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    // Contact Info
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    // Equipment Info
    brand: '',
    model: '',
    engineType: '',
    horsepower: '',
    year: '',
    hours: '',
    condition: '',
    serialNumber: '',
    askingPrice: '',
    description: '',
    // Additional
    hasDocumentation: false,
    availableForInspection: false,
    preferredContact: 'email'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 6) {
      toast.error('Maximum 6 images allowed');
      return;
    }
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.brand || !formData.model) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/sell-requests`, {
        ...formData,
        imageCount: images.length
      });
      
      setSubmitted(true);
      toast.success('Equipment submission received! We will contact you within 24 hours.');
    } catch (error) {
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="bg-slate-50 min-h-screen">
        <div className="container-custom py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">Submission Received!</h1>
            <p className="text-lg text-slate-600 mb-8">
              Thank you for submitting your equipment. Our team will review the details and 
              contact you within 24 hours with a valuation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button className="bg-orange-500 hover:bg-orange-600">
                  Return Home
                </Button>
              </Link>
              <Link to="/inventory">
                <Button variant="outline">
                  Browse Inventory
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-slate-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-green-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full text-green-400 text-sm font-medium mb-6">
              <DollarSign className="w-4 h-4" />
              Turn Your Equipment Into Cash
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Sell Your
              <span className="text-orange-500"> Marine Equipment</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Get the best value for your marine engines, generators, and equipment. 
              We offer competitive prices, fast payment, and hassle-free transactions - 
              including cryptocurrency payments.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Free Valuation
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                24hr Response
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Crypto Accepted
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Global Pickup
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Form Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Equipment Details</h2>
                <p className="text-slate-600 mb-8">Fill out the form below and we&apos;ll get back to you with a valuation.</p>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-orange-500" />
                      Contact Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Marine Services LLC"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="City, Country"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Equipment Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Ship className="w-5 h-5 text-orange-500" />
                      Equipment Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Brand *</Label>
                        <Select onValueChange={(v) => handleSelectChange('brand', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select brand" />
                          </SelectTrigger>
                          <SelectContent>
                            {brands.map(brand => (
                              <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="model">Model *</Label>
                        <Input
                          id="model"
                          name="model"
                          value={formData.model}
                          onChange={handleChange}
                          placeholder="e.g., C32, KTA50-M"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Equipment Type</Label>
                        <Select onValueChange={(v) => handleSelectChange('engineType', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {engineTypes.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Condition *</Label>
                        <Select onValueChange={(v) => handleSelectChange('condition', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            {conditions.map(cond => (
                              <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="horsepower">Horsepower</Label>
                        <Input
                          id="horsepower"
                          name="horsepower"
                          value={formData.horsepower}
                          onChange={handleChange}
                          placeholder="e.g., 1000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="year">Year</Label>
                        <Input
                          id="year"
                          name="year"
                          value={formData.year}
                          onChange={handleChange}
                          placeholder="e.g., 2018"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="hours">Hours</Label>
                        <Input
                          id="hours"
                          name="hours"
                          value={formData.hours}
                          onChange={handleChange}
                          placeholder="e.g., 5000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="serialNumber">Serial Number</Label>
                        <Input
                          id="serialNumber"
                          name="serialNumber"
                          value={formData.serialNumber}
                          onChange={handleChange}
                          placeholder="Optional"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="askingPrice">Asking Price (USD)</Label>
                        <Input
                          id="askingPrice"
                          name="askingPrice"
                          value={formData.askingPrice}
                          onChange={handleChange}
                          placeholder="e.g., 50000 (or 'Open to offers')"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Describe your equipment's history, maintenance, and any additional details..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Camera className="w-5 h-5 text-orange-500" />
                      Photos (up to 6)
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {images.map((img, index) => (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden border-2 border-slate-200">
                          <img src={img.preview} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {images.length < 6 && (
                        <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-orange-500 cursor-pointer flex flex-col items-center justify-center text-slate-400 hover:text-orange-500 transition-colors">
                          <Upload className="w-8 h-8 mb-2" />
                          <span className="text-xs">Add Photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Additional Options */}
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="hasDocumentation"
                        checked={formData.hasDocumentation}
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-slate-700">Has service documentation</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="availableForInspection"
                        checked={formData.availableForInspection}
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-slate-700">Available for inspection</span>
                    </label>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit for Valuation
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* How It Works */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                <h3 className="text-xl font-bold text-slate-900 mb-6">How It Works</h3>
                <div className="space-y-6">
                  {howItWorks.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-orange-600">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">{item.title}</h4>
                        <p className="text-sm text-slate-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Methods */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-white mb-4">Payment Options</h3>
                <p className="text-slate-300 text-sm mb-6">
                  We offer flexible payment options including cryptocurrency for fast, secure transactions.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-slate-300">
                    <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <span className="text-orange-400 font-bold text-xs">₿</span>
                    </div>
                    <span>Bitcoin (BTC)</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <span className="text-blue-400 font-bold text-xs">$</span>
                    </div>
                    <span>Bank Wire Transfer</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                      <span className="text-green-400 font-bold text-xs">LC</span>
                    </div>
                    <span>Letter of Credit</span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-orange-50 rounded-2xl p-8 border border-orange-100">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Need Help?</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Have questions? Our equipment specialists are here to help.
                </p>
                <div className="space-y-3">
                  <a href="tel:+12012344851" className="flex items-center gap-3 text-slate-700 hover:text-orange-600">
                    <Phone className="w-5 h-5" />
                    +1 (201) 234-4851
                  </a>
                  <a href="mailto:sell@engineshipparts.com" className="flex items-center gap-3 text-slate-700 hover:text-orange-600">
                    <Mail className="w-5 h-5" />
                    sell@engineshipparts.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
